import { createAsyncThunk } from "@reduxjs/toolkit";

import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import {
    GetAllEmployeeAssets,
    GetEmployeeAssetByID,
    CreateNewEmployeeAsset,
    UpdateEmployeeAsset,
    ReturnEmployeeAsset,
    DeleteEmployeeAsset,
    AssignEmployeeAssetToEmployee,
    GetEmployeeAssets,
    InitiateEmployeeAssetTransfer,
    ConfirmEmployeeAssetTransfer,
    RejectEmployeeAssetTransfer,
    GetEmployeeAssetTransferHistory,
    GetEmployeeAssetHistory,
    GetPendingEmployeeTransfers,
    GetMyEmployeeTransfers,
    DeleteEmployeeAssetTransfer
} from "helpers/api_method";

export const GetAllAssetsList = createAsyncThunk("assets/GetAllAssetsList", async () => {
    try {
        const response = await GetAllEmployeeAssets();
        // Handle the actual API response structure
        if (response.data && response.data.status) {
            return response.data.data;
        }
        return response.data;
    } catch (error) {
        return error;
    }
});

export const GetEmployeeAssetsList = createAsyncThunk("assets/GetEmployeeAssetsList", async (employeeId: string) => {
    try {
        const response = await GetEmployeeAssets(employeeId);
        return response.data;
    } catch (error) {
        return error;
    }
});

export const GetAssetByIDThunk = createAsyncThunk("assets/GetAssetByID", async (id: string) => {
    try {
        const response = await GetEmployeeAssetByID(id);
        // Handle the actual API response structure
        if (response.data && response.data.status) {
            return response.data.data;
        }
        return response.data;
    } catch (error) {
        return error;
    }
});
export const addNewAsset = createAsyncThunk("assets/addNewAsset", async (event: any) => {
    try {
        const response = await CreateNewEmployeeAsset(event);
        toast.success("Asset created Successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Asset creation Failed", { autoClose: 2000 });
        return error;
    }
});
export const updateExistingAssets = createAsyncThunk("assets/updateExistingAssets", async (event: any) => {
    try {
        const response = await UpdateEmployeeAsset(event.id, event);
        toast.success("Asset updated Successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Asset updated Failed", { autoClose: 2000 });
        return error;
    }
});
export const assignAsset = createAsyncThunk("assets/assignAsset", async (event: any) => {
    try {
        // Check if this is an unassign operation (assigned_to is null)
        if (event.assigned_to === null) {
            // Use the dedicated /return endpoint to properly reset asset status to Available
            const response = await ReturnEmployeeAsset(event.id, {
                return_date: event.unassigned_date || new Date().toISOString().split('T')[0],
                return_notes: event.unassign_reason
            });
            
            toast.success("Asset unassigned successfully", { autoClose: 2000 });
            return {
                ...response.data,
                assignmentData: event,
                methodUsed: 'ReturnEmployeeAsset'
            };
        } else {
            // Use the correct Employee Asset assignment API for assignment
            const response = await AssignEmployeeAssetToEmployee(event.asset_id, {
                assigned_to: event.assigned_to,
                assignment_date: event.assignment_date
            });
            
            toast.success("Asset assigned Successfully", { autoClose: 2000 });
            
            // After successful assignment, fetch the updated asset data
            try {
                const updatedAssetResponse = await GetEmployeeAssetByID(event.asset_id);
                return {
                    ...response.data,
                    assignmentData: event,
                    updatedAsset: updatedAssetResponse.data,
                    methodUsed: 'AssignEmployeeAssetToEmployee'
                };
            } catch (fetchError) {
                return {
                    ...response.data,
                    assignmentData: event,
                    methodUsed: 'AssignEmployeeAssetToEmployee'
                };
            }
        }
    } catch (error) {
        const action = event.assigned_to === null ? "unassign" : "assign";
        toast.error(`Asset ${action} Failed`, { autoClose: 2000 });
        return error;
    }
});
export const deleteExistingAsset = createAsyncThunk("assets/deleteExistingAsset", async (event: any) => {
    try {
        console.log('DeleteExistingAsset thunk called with ID:', event.id, 'Type:', typeof event.id);
        
        // Delete the asset and all its related data
        const response = await DeleteEmployeeAsset(event.id);
        console.log('Delete API response:', response);
        
        // Check if the API call was successful
        if (response.data && response.data.status === false) {
            // Backend returned an error
            const errorMessage = response.data.message || 'Asset deletion failed';
            console.error('Backend delete error:', errorMessage);
            
            // Check if it's a foreign key constraint error
            if (errorMessage.includes('foreign key constraint fails') || errorMessage.includes('IntegrityError')) {
                toast.error('Cannot delete asset: This asset has related records that prevent deletion. Please ensure the asset is not assigned and try again.', { autoClose: 5000 });
                console.error('Database constraint error:', errorMessage);
            } else {
                toast.error(errorMessage, { autoClose: 3000 });
            }
            throw new Error(errorMessage);
        }
        
        // The backend should handle cascading deletes for:
        // - Asset assignments
        // - Asset history
        // - Asset transfer history
        // - Asset transfer requests
        // - Any other related records
        
        toast.success("Asset and all related history deleted successfully", { autoClose: 2000 });
        return {
            ...response.data,
            deletedAssetId: event.id,
            message: "Asset and all related history have been permanently removed"
        };
    } catch (error: any) {
        console.error('Delete asset error:', error);
        console.error('Error details:', {
            message: error?.message,
            status: error?.response?.status,
            data: error?.response?.data,
            url: error?.config?.url
        });
        
        // Don't show duplicate error messages if backend already showed one
        if (!error?.message?.includes('Error deleting asset')) {
            toast.error("Asset deletion failed. Please try again.", { autoClose: 2000 });
        }
        throw error; // Re-throw the error so it can be caught by the component
    }
});

// Asset Transfer Thunks
export const initiateAssetTransfer = createAsyncThunk("assets/initiateAssetTransfer", async (data: any) => {
    try {
        const response = await InitiateEmployeeAssetTransfer(data);
        // Handle the actual API response structure
        if (response.data && response.data.status) {
            toast.success(response.data.message || "Asset transfer initiated successfully", { autoClose: 2000 });
            return response.data.data;
        }
        toast.success("Asset transfer initiated successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to initiate asset transfer", { autoClose: 2000 });
        return error;
    }
});

export const confirmAssetTransfer = createAsyncThunk("assets/confirmAssetTransfer", async (data: any) => {
    try {
        const response = await ConfirmEmployeeAssetTransfer(data.transferId, data);
        toast.success("Asset transfer confirmed successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to confirm asset transfer", { autoClose: 2000 });
        return error;
    }
});

export const rejectAssetTransfer = createAsyncThunk("assets/rejectAssetTransfer", async (data: any) => {
    try {
        const response = await RejectEmployeeAssetTransfer(data.transferId, data);
        toast.success("Asset transfer rejected successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to reject asset transfer", { autoClose: 2000 });
        return error;
    }
});

// Asset History Thunks
export const getAssetTransferHistory = createAsyncThunk("assets/getAssetTransferHistory", async (assetId: string) => {
    try {
        const response = await GetEmployeeAssetTransferHistory(assetId);
        // Handle the actual API response structure
        if (response.data && response.data.status) {
            return response.data.data;
        }
        return response.data;
    } catch (error) {
        toast.error("Failed to fetch transfer history", { autoClose: 2000 });
        return error;
    }
});

export const getAssetHistory = createAsyncThunk("assets/getAssetHistory", async (assetId: string) => {
    try {
        const response = await GetEmployeeAssetHistory(assetId);
        // Handle the actual API response structure
        if (response.data && response.data.status) {
            return response.data.data;
        }
        return response.data;
    } catch (error) {
        toast.error("Failed to fetch asset history", { autoClose: 2000 });
        return error;
    }
});

export const getPendingTransfers = createAsyncThunk("assets/getPendingTransfers", async () => {
    try {
        const response = await GetPendingEmployeeTransfers();
        return response.data;
    } catch (error) {
        toast.error("Failed to fetch pending transfers", { autoClose: 2000 });
        return error;
    }
});

export const getMyTransfers = createAsyncThunk("assets/getMyTransfers", async () => {
    try {
        const response = await GetMyEmployeeTransfers();
        return response.data;
    } catch (error) {
        toast.error("Failed to fetch my transfers", { autoClose: 2000 });
        return error;
    }
});

export const deletePendingTransfer = createAsyncThunk("assets/deletePendingTransfer", async (transferId: string) => {
    try {
        const response = await DeleteEmployeeAssetTransfer(transferId);
        toast.success("Pending transfer deleted", { autoClose: 2000 });
        return { transferId, ...(response?.data || {}) };
    } catch (error) {
        toast.error("Failed to delete pending transfer", { autoClose: 2000 });
        return error;
    }
});